import type { AuthUser } from '@rudi/auth';
import { readTargetId } from './target';

/**
 * Session persistence for the Cortex app.
 *
 * @rudi/auth's client stores only the tokens via the PlatformAdapter; the user
 * profile lives in React state and is lost on reload. We persist the user here
 * so a refresh can rehydrate `initialState` synchronously (the provider then
 * verifies the token in the background).
 *
 * Each Rudi connection has its own Keycloak realm, so each keeps its own
 * session under keys suffixed with the connection id. The server-configured
 * connection keeps the unsuffixed keys, so existing sessions stay valid.
 */

export const TOKENS_KEY = 'cortex_tokens_v2';
export const USER_KEY = 'cortex_user_v2';

export function sessionKey(base: string, targetId: string = readTargetId()): string {
  return targetId ? `${base}:${targetId}` : base;
}

/** Whether this browser holds a refreshable session for a connection. */
export function hasSession(targetId: string): boolean {
  try {
    const raw = localStorage.getItem(sessionKey(TOKENS_KEY, targetId));
    return !!raw && !!(JSON.parse(raw) as { refreshToken?: string | null }).refreshToken;
  } catch {
    return false;
  }
}

export interface CortexInitialState {
  user: AuthUser;
  accessToken: string;
  refreshToken: string;
}

export function persistUser(user: AuthUser): void {
  try {
    localStorage.setItem(sessionKey(USER_KEY), JSON.stringify(user));
  } catch {
    // Ignore storage errors.
  }
}

/** Forget the session of the current connection only. */
export function clearSession(): void {
  try {
    localStorage.removeItem(sessionKey(TOKENS_KEY));
    localStorage.removeItem(sessionKey(USER_KEY));
    localStorage.removeItem('rudi_cortex_tokens');
    localStorage.removeItem('rudi_cortex_user');
  } catch {
    // Ignore storage errors.
  }
}

/** Synchronously read a persisted session, or null if incomplete. */
export function readInitialState(): CortexInitialState | null {
  try {
    const tokensRaw = localStorage.getItem(sessionKey(TOKENS_KEY));
    const userRaw = localStorage.getItem(sessionKey(USER_KEY));
    if (!tokensRaw || !userRaw) return null;
    const tokens = JSON.parse(tokensRaw) as {
      accessToken?: string;
      refreshToken?: string;
    };
    const user = JSON.parse(userRaw) as AuthUser;
    if (!tokens.accessToken || !tokens.refreshToken || !user) return null;
    return {
      user,
      accessToken: tokens.accessToken,
      refreshToken: tokens.refreshToken,
    };
  } catch {
    return null;
  }
}
